// File: /src/pages/Login.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Eye, EyeOff } from 'lucide-react';
import '../styles/CustomToastify.css';
import '../styles/Login.css';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

function Login() {
	const { isDarkMode } = useTheme();
	const { login } = useAuth();
	const [form, setForm] = useState({
		name: '',
		email: '',
		password: '',
		cpf_cnpj: '',
	});
	const [isRegisterMode, setIsRegisterMode] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();

	// Password validation: min 8 chars with uppercase, lowercase, number, and any special character
	// Accepts ANY non-alphanumeric character as special (more flexible and user-friendly)
	const passwordRegex =
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

	// Atualiza o estado do formulário
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setForm({ ...form, [name]: value });
	};

	// Função para login do usuário
	const handleLogin = async (e) => {
		e.preventDefault();
		try {
			const response = await apiClient.post(API_ENDPOINTS.AUTH_LOGIN, {
				email: form.email,
				password: form.password,
			});
			const { token, user } = response.data;
			await login(token, user);
			toast.success(`Bem-vindo(a), ${user.name}!`);
			navigate('/');
		} catch (error) {
			toast.error(error.response?.data?.error || 'Erro ao fazer login.');
		}
	};

	// Função para registrar um novo usuário
	const handleRegister = async (e) => {
		e.preventDefault();

		if (!passwordRegex.test(form.password)) {
			toast.error(
				'A senha deve ter no mínimo 8 caracteres, incluindo: 1 letra maiúscula, 1 minúscula, 1 número e 1 caractere especial.'
			);
			return;
		}

		try {
			await apiClient.post(API_ENDPOINTS.AUTH_REGISTER, {
				name: form.name,
				email: form.email,
				password: form.password,
				cpf_cnpj: form.cpf_cnpj,
			});
			toast.success('Usuário registrado com sucesso! Faça login.');
			setIsRegisterMode(false);
			setForm({ name: '', email: '', password: '', cpf_cnpj: '' });
		} catch (error) {
			console.error('❌ Erro ao registrar usuário:', error.response);
			const errorMessage =
				error.response?.data?.details ||
				error.response?.data?.error ||
				'Erro ao registrar usuário.';
			toast.error(errorMessage);
		}
	};

	// Função para recuperação de senha
	const handleForgotPassword = async () => {
		if (!form.email) {
			toast.error('Por favor, insira seu e-mail.');
			return;
		}

		try {
			const response = await apiClient.post(
				API_ENDPOINTS.AUTH_FORGOT_PASSWORD,
				{
					email: form.email,
				}
			);
			toast.success(response.data.message);
		} catch (error) {
			toast.error(
				error.response?.data?.error || 'Erro ao recuperar senha.'
			);
		}
	};

	return (
		<div className={`auth-page ${isDarkMode ? 'dark-mode' : ''}`}>
			<div className="auth-logo">
				<img
					src="/locatime1.png"
					alt="LocaTime"
					loading="lazy"
				/>
			</div>
			<div className="login-container">
				<h2>{isRegisterMode ? 'Registrar' : 'Login'}</h2>
				<form onSubmit={isRegisterMode ? handleRegister : handleLogin}>
					{isRegisterMode && (
						<>
							<input
								name="name"
								type="text"
								placeholder="Nome"
								value={form.name}
								onChange={handleInputChange}
								required
							/>
							<input
								name="cpf_cnpj"
								type="text"
								placeholder="CPF ou CNPJ"
								value={form.cpf_cnpj}
								onChange={handleInputChange}
								required
							/>
						</>
					)}
					<input
						name="email"
						type="email"
						placeholder="Email"
						value={form.email}
						onChange={handleInputChange}
						required
					/>
					<div className="password-input-wrapper">
						<input
							name="password"
							type={showPassword ? 'text' : 'password'}
							placeholder="Senha"
							value={form.password}
							onChange={handleInputChange}
							required
						/>
						<span
							className="password-toggle-icon"
							onClick={() => setShowPassword(!showPassword)}
						>
							{showPassword ? (
								<EyeOff size={20} />
							) : (
								<Eye size={20} />
							)}
						</span>
					</div>
					<button type="submit">
						{isRegisterMode ? 'Registrar' : 'Entrar'}
					</button>
				</form>
				{!isRegisterMode && (
					<p className="forgot-password" onClick={handleForgotPassword}>
						Esqueci minha senha
					</p>
				)}
				<p>
					{isRegisterMode
						? 'Já possui uma conta? '
						: 'Ainda não possui uma conta? '}
					<span
						className="toggle-mode"
						onClick={() => setIsRegisterMode(!isRegisterMode)}
					>
						{isRegisterMode ? 'Faça login' : 'Registre-se'}
					</span>
				</p>
			</div>
			<div className="auth-powered-by">
				<span>Powered by:</span>
				<a
					href="https://www.deeplynx.com.br/pt/"
					target="_blank"
					rel="noopener noreferrer"
					className="auth-powered-by__link"
				>
					<img
						src="/deeplynx.png"
						alt="DeepLynx"
						className="auth-powered-by__logo"
						loading="lazy"
					/>
				</a>
			</div>
		</div>
	);
}

export default Login;
